"use client";

import React from "react";
import { ArrowRight, Bot, CircleCheck as CheckCircle2, Database, Search, Sparkles, TrendingUp, Workflow, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const systemPillars = [{
  id: "01",
  title: "Generative Engine Optimization",
  shortTag: "GEO Visibility",
  description: "Position your business as the primary recommended solution when local clients prompt ChatGPT, Claude, Perplexity, and Gemini.",
  iconName: "Search",
  linkUrl: "/GenerativeEngineOptimizationPage"
}, {
  id: "02",
  title: "AI Receptionist & Chat",
  shortTag: "Instant Engagement",
  description: "Qualify inbound leads 24/7 with automated voice and text engagement that schedules appointments directly on your calendar.",
  iconName: "Bot",
  linkUrl: "/AISolutionsReceptionistChatPage"
}, {
  id: "03",
  title: "Unified Lead CRM",
  shortTag: "Pipeline Control",
  description: "Centralize customer interactions, deal stages, and customer history in one pipeline so no revenue opportunity falls behind.",
  iconName: "Database",
  linkUrl: "/CrmLeadManagementPage"
}, {
  id: "04",
  title: "Automated Workflows",
  shortTag: "Business Automations",
  description: "Deploy automated review requests, appointment confirmations, and reactivation campaigns that drive repeat local business.",
  iconName: "Workflow",
  linkUrl: "/BusinessAutomationsPage"
}];
const systemStats = [{
  number: "3.8x",
  label: "AI Recommendation Rate",
  detail: "Increased inclusion across ChatGPT, Perplexity, and Gemini local answers"
}, {
  number: "< 45s",
  label: "Lead Response Time",
  detail: "Instant AI engagement across web chat, SMS, and missed call text-backs"
}, {
  number: "+142%",
  label: "Calendar Conversions",
  detail: "More qualified bookings synchronized directly into your CRM pipeline"
}];
export default function Synergy() {
  const shouldReduceMotion = useReducedMotion();
  const getPillarIcon = (name: string) => {
    switch (name) {
      case "Search":
        return <Search className="size-5 text-[var(--accent-deep-red)]" />;
      case "Bot":
        return <Bot className="size-5 text-[var(--accent-deep-red)]" />;
      case "Database":
        return <Database className="size-5 text-[var(--accent-deep-red)]" />;
      case "Workflow":
        return <Workflow className="size-5 text-[var(--accent-deep-red)]" />;
      default:
        return <Zap className="size-5 text-[var(--accent-deep-red)]" />;
    }
  };
  return <section id="growthsystemsynergy" data-nav="dark" className="relative overflow-hidden bg-background py-24 text-foreground sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(130,39,51,0.18),transparent)]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.08)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.08)_1px,transparent_1px)] bg-[size:3rem_3rem]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 gap-1.5 rounded-full border-[var(--accent-deep-red)]/40 bg-[var(--accent-deep-red)]/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-foreground">
            <Sparkles className="size-3 text-[var(--accent-deep-red)]" />
            ClearLine Growth Architecture
          </Badge>

          <h2 className="font-serif text-3xl font-normal tracking-tight text-foreground sm:text-5xl md:text-6xl">
            When CRM, GEO &amp; AI Work <br className="hidden sm:inline" />
            <span className="italic text-[var(--accent-deep-red)]">In Perfect Synergy</span>
          </h2>

          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Isolated marketing tactics leave gaps. We connect generative AI search visibility directly to automated lead capture and CRM workflows so every high-intent buyer is found, qualified, and booked.
          </p>
        </motion.div>

        {}
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {systemPillars.map((pillar, i) => <motion.div key={pillar.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 28
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1,
          ease: "easeOut"
        }} className="flex">
              <Card className="group relative flex flex-col justify-between overflow-hidden rounded-xl border border-border/80 bg-card/60 p-6 shadow-xl transition-all duration-300 hover:border-[var(--accent-deep-red)]/60 hover:bg-card hover:shadow-2xl">
                <div>
                  <div className="flex items-center justify-between pb-4">
                    <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-muted/50 transition-colors group-hover:border-[var(--accent-deep-red)]/40 group-hover:bg-[var(--accent-deep-red)]/10">
                      {getPillarIcon(pillar.iconName)}
                    </div>
                    <span className="font-mono text-xs font-semibold tracking-widest text-muted-foreground group-hover:text-[var(--accent-deep-red)]">
                      {pillar.id}
                    </span>
                  </div>

                  <Badge variant="secondary" className="mb-3 font-mono text-[10px] tracking-wider uppercase">
                    {pillar.shortTag}
                  </Badge>

                  <CardHeader className="p-0">
                    <CardTitle className="font-default text-lg font-semibold text-card-foreground group-hover:text-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="mt-3 p-0">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {pillar.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <CardFooter className="mt-6 p-0 pt-4">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between gap-1 p-0 text-xs font-medium text-foreground hover:bg-transparent hover:text-[var(--accent-deep-red)]">
                    <Link to={pillar.linkUrl}>
                      <span>Explore Pillar</span>
                      <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-16 rounded-2xl border border-border/60 bg-card/40 p-6 shadow-xl backdrop-blur-sm sm:p-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {systemStats.map((stat, i) => <div key={stat.label} data-index={i} className="relative flex flex-col justify-between border-b border-border/40 pb-6 md:border-b-0 md:border-r md:pb-0 md:pr-6 md:last:border-r-0 md:last:pr-0">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                      {stat.number}
                    </span>
                    <TrendingUp className="size-5 text-[var(--accent-deep-red)]" />
                  </div>
                  <h3 className="mt-2 text-sm font-semibold text-foreground">
                    {stat.label}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {stat.detail}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>

        {}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 28
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.3,
        ease: "easeOut"
      }} className="relative mt-16 overflow-hidden rounded-2xl border border-[var(--accent-deep-red)]/40 bg-gradient-to-b from-card to-background p-8 text-center shadow-2xl sm:p-12">
          {}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent-deep-red)] to-transparent" />

          <h3 className="font-serif text-2xl font-normal tracking-tight text-foreground sm:text-4xl">
            Ready to unify your AI visibility and lead engine?
          </h3>

          <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
            Stop losing potential customers to competitors recommended by AI. Get a complete analysis of your brand presence across ChatGPT, Perplexity, and local search engines.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="w-full bg-[var(--accent-deep-red)] text-white shadow-md hover:bg-[var(--accent-deep-red)]/90 sm:w-auto">
              <Link to="/ContactPage">
                <span>Get started </span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground sm:w-auto">
              <Link to="/VisibilityCheckupPage">
                <span>Get Free AI Checkup</span>
              </Link>
            </Button>
          </div>

          <Separator className="my-8 bg-border/60" />

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-[var(--accent-deep-red)]" />
              NO LONG-TERM CONTRACTS
            </span>
            <span className="hidden sm:inline">&bull;</span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-[var(--accent-deep-red)]" />
              48-HOUR SYSTEM SETUP
            </span>
            <span className="hidden sm:inline">&bull;</span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-[var(--accent-deep-red)]" />
              AUDIT DELIVERED IN 1 BUSINESS DAY
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}